import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { Observable, of } from 'rxjs';
import { RootState } from '../';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { OrderRestService } from '../../services/rest/order.rest.service';
import { AbstractEffects } from '../AbstractEffects';
import * as actions from './orders.actions';
import { OrderAcceptDeviationAction, OrderRejectDeviationAction, OrdersDownloadAction } from './orders.actions';
import { catchError, debounceTime, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';
import { HttpErrorResponse } from '@angular/common/http';
import moment from 'moment';
import { AuthenticationService } from '../../authentication/authentication.service';

@Injectable({ providedIn: 'root' })
export class OrdersEffects extends AbstractEffects {
  private authenticationService = inject(AuthenticationService);
  loadOrders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.ORDERS_LOAD),
      debounceTime(50), // Avoid double click
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        let customers = null;
        if (state.session.userContext.isSuperUser) {
          customers = state.session.customerPermissions.data.queryOrGetOrder;
        }
        return this.orderRestService.getOrders(state.orders.filter, customers).pipe(
          map(
            orders =>
              new actions.OrdersLoadSuccessAction({
                orders,
                definitions: state.definitions,
                places: state.contractDetails.places,
              })
          ),
          catchError(err => this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.OrdersLoadFailAction(err))]))
        );
      })
    )
  );
  downloadOrders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrdersDownloadAction>(actions.ORDERS_DOWNLOAD),
      debounceTime(50),
      withLatestFrom(this.authenticationService.authenticated$, this.store, (action, isAuthenticated, state) => ({
        action,
        isAuthenticated,
        state,
      })),
      filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
      switchMap(({ action, state }: { action: OrdersDownloadAction; state: RootState; isAuthenticated: boolean }) => {
        let customers: Customer[] = null;
        if (state.session.userContext.isSuperUser) {
          customers = state.session.customerPermissions.data.queryOrGetOrder;
        }
        return this.orderRestService.getOrdersAsDocument(action.payload.filter, action.payload.totalItems, action.payload.exportType, customers).pipe(
          map((blob: Blob) => {
            const timestamp = moment().format('YYYYMMDDHHmmss');
            return new actions.OrdersDownloadSuccessAction({ filename: `orders-${timestamp}.xlsx`, blob });
          }),
          catchError(err => this.handleFail(err, `ERRORS.${action.payload.exportType?.toUpperCase()}_DOWNLOAD_FAILED.TITLE`, [of(new actions.OrdersDownloadFailAction(err))]))
        );
      })
    )
  );

  acceptDeviation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderAcceptDeviationAction>(actions.ORDER_ACCEPT_DEVIATION),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => ({ action, state })),
      switchMap(({ action, state }) =>
        this.orderRestService.acceptDeviation(action.payload.ccrNumber).pipe(
          map(() => new actions.OrderAcceptDeviationSuccessAction({
            ccrNumber: action.payload.ccrNumber,
            definitions: state.definitions
          })),
          catchError((error: HttpErrorResponse) =>
            this.handleFail(error, 'ORDERS.LIST.LABELS.ACCEPT_DEVIATION', [
              of(
                new actions.OrderAcceptDeviationFailAction({
                  error,
                  ccrNumber: action.payload.ccrNumber,
                })
              ),
            ])
          )
        )
      )
    )
  );

  rejectDeviation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<OrderRejectDeviationAction>(actions.ORDER_REJECT_DEVIATION),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => ({ action, state })),
      switchMap(({ action, state }) =>
        this.orderRestService.rejectDeviation(action.payload.ccrNumber).pipe(
          map(() => new actions.OrderRejectDeviationSuccessAction({
            ccrNumber: action.payload.ccrNumber,
            definitions: state.definitions
          })),
          catchError((error: HttpErrorResponse) =>
            this.handleFail(error, 'ORDERS.LIST.LABELS.REJECT_DEVIATION', [
              of(
                new actions.OrderRejectDeviationFailAction({
                  error,
                  ccrNumber: action.payload.ccrNumber,
                })
              ),
            ])
          )
        )
      )
    )
  );

  loadLatestFlows$: Observable<{}> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DASHBOARD_TILE_LATEST_ORDERS_LOAD),
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        return this.orderRestService.getLatestOrders(state.session.selectedCustomer.loadSuccess && state.session.selectedCustomer.data?.id).pipe(
          map(orders => {
            return new actions.DasboardTileLatestOrdersLoadSuccess({ orders });
          }),
          catchError(err => {
            return of(new actions.DasboardTileLatestOrdersLoadFail(err));
            // this.handleFail(err, 'Get Latest Flows', [of(new actions.DasboardTileLatestOrdersLoadFail(err))])
          })
        );
      })
    )
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private orderRestService: OrderRestService,
    translateService: TranslateService,
    public dialog: MatDialog,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
